import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import BaseService from '@/services/common/BaseService';
import { IActivity } from '@/utils/activity-calendar/interfaces/IActivity';
import { ICalendarFarm } from '@/utils/activity-calendar/interfaces/ICalendarFarm';
import { ActivityStatus, ActivityType, ApiViewMode } from '@/utils/activity-calendar/enums/ActivityCalendar.enum';

enum ActivityRecordGroup {
  DryApplication = 'Dry Application',
  Fertigation = 'Fertigation',
}

enum ApiRecordStatus {
  Completed = 'completed',
}

interface ApiItem {
  sprayRecordId: string | null;
  dryApplicationRecordId: string | null;
  fertigationRecordId: string | null;
  recordNumberId: number | null;
  recordGroup: string;
  activity: string;
  jobName: string;
  farmId: number;
  farmName: string;
  date: string;
  recordStatus: string;
}

const toType = (recordGroup: string): ActivityType => {
  if (recordGroup === ActivityRecordGroup.DryApplication) return ActivityType.Dry;
  if (recordGroup === ActivityRecordGroup.Fertigation) return ActivityType.Fertigation;
  return ActivityType.Spray;
};

const mapItem = (item: ApiItem, index: number): IActivity => {
  const d = dayjs(item.date);
  const year = d.year();
  const monthIndex = d.month();
  const dayOfMonth = d.date();
  const timestamp = d.startOf('day').valueOf();
  return {
    id: item.recordNumberId ?? -(index + 1),
    year,
    monthIndex,
    dayOfMonth,
    dateKey: `${year}-${monthIndex}-${dayOfMonth}`,
    type: toType(item.recordGroup),
    farm: item.farmName,
    block: item.activity,
    title: item.jobName,
    status: item.recordStatus?.toLowerCase() === ApiRecordStatus.Completed ? ActivityStatus.Completed : ActivityStatus.Planned,
    timestamp,
    recordId: item.sprayRecordId ?? item.dryApplicationRecordId ?? item.fertigationRecordId ?? null,
  };
};

interface CacheEntry {
  activities: IActivity[];
  farms: ICalendarFarm[];
}

const windowCache = new Map<string, CacheEntry>();

export const invalidateActivityCalendarCache = () => {
  windowCache.clear();
};

export interface UseActivityCalendarResult {
  activities: IActivity[];
  farms: ICalendarFarm[];
  loading: boolean;
  error: string | null;
}

export const useActivityCalendar = (
  fromDate: string,
  untilDate: string,
  viewMode: ApiViewMode,
): UseActivityCalendarResult => {
  const cacheKey = `${fromDate}|${untilDate}|${viewMode}`;

  const [activities, setActivities] = useState<IActivity[]>([]);
  const [farms, setFarms] = useState<ICalendarFarm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = windowCache.get(cacheKey);

    if (cached) {
      setActivities(cached.activities);
      setFarms(cached.farms);
      setLoading(false);
    } else {
      setActivities([]);
      setFarms([]);
      setLoading(true);
    }
    setError(null);

    BaseService.get('/ActivityCalendar', {
      params: { fromDate, untilDate, viewMode },
    })
      .then((res) => {
        if (cancelled) return;

        const data = res.data;
        const mappedActivities = (data.activities ?? []).map(mapItem);
        const mappedFarms: ICalendarFarm[] = data.farms ?? [];

        windowCache.set(cacheKey, { activities: mappedActivities, farms: mappedFarms });

        setFarms(mappedFarms);
        setActivities(mappedActivities);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!cached) {
          const message =
            typeof err?.response?.data === 'string'
              ? err.response.data
              : err?.message ?? 'Failed to load activity calendar';
          setError(message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, fromDate, untilDate, viewMode]);

  return { activities, farms, loading, error };
};
